import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { H3, IntegrationCard, Text } from 'components';
import TipCalculatorSiteSelector from 'components/SiteSelector/TipCalculatorSiteSelector';
import { appConfig } from 'config/app';
import { POS_CATEGORIES } from 'config/const';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getIntegrationAppsRequest, Integrations, listSiteIntegrationsRequest, selectIntegrationApps, selectSiteIntegrations } from 'store/Collections/Integrations';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { ApiQueryOperator } from 'types';
import { SectionTitle, SettingsContent, SettingsContentLayout, SettingsPageHeader, SettingsSection, SettingsSectionContent, SettingsSectionHeader } from '../../Settings/components';
import { DisabledWrapper, LoadingSkeleton, StyledLink } from '../styles';

const IntegrationsSettings = () => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const apps = useSelector(selectIntegrationApps);
  const posIntegrations = useSelector(selectSiteIntegrations(currentSite?.id ?? '')) || [];
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Integrations.LIST_SITE_INTEGRATIONS_REQUEST));

  // The apps catalog is global; fetch once (saga serves from cache thereafter).
  useEffect(() => {
    dispatch(getIntegrationAppsRequest());
  }, [dispatch]);

  // Fetch the selected site's POS integrations
  useEffect(() => {
    if (currentSite?.id) dispatch(listSiteIntegrationsRequest(currentSite.id, { q: [{ key: 'categories', operator: ApiQueryOperator.in, value: POS_CATEGORIES }] }));
  }, [dispatch, currentSite?.id]);

  const hasIntegrations = posIntegrations.length > 0;

  return (
    <SettingsContent data-cy="integrations-settings-container">
      <SettingsPageHeader />

      <SettingsContentLayout>
        <SettingsSection>
          <SettingsSectionHeader>
            <H3>Integrations</H3>
          </SettingsSectionHeader>

          <SettingsSectionContent light data-cy="integrations-settings-content">
            <SectionTitle>Select Site</SectionTitle>
            <TipCalculatorSiteSelector />

            {!currentSite || isLoading ? (
              <LoadingSkeleton />
            ) : hasIntegrations ? (
              posIntegrations.map((integration) => <IntegrationCard key={integration.id} integration={integration} apps={apps} hideHiddenSettings />)
            ) : (
              <DisabledWrapper data-cy="integrations-settings-empty">
                <Text size="small">
                  {'There are no integrations for this site. '}
                  <StyledLink target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.SUPPORT}>
                    Contact Kickfin Support
                  </StyledLink>
                  {' to learn about supported integrations.'}
                </Text>
              </DisabledWrapper>
            )}
          </SettingsSectionContent>
        </SettingsSection>
      </SettingsContentLayout>
    </SettingsContent>
  );
};

export default IntegrationsSettings;
